/**
 * vintageLabelFor — format a data-vintage timestamp for the
 * "Verified {quarter/month}" chip that appears across the app
 * (firm salary bands, firm compare, country cost-of-living).
 *
 * Accepts common serialisation shapes:
 *   "2025-Q4"       → "Q4 2025"
 *   "2026-01"       → "Jan 2026"
 *   "2026-01-15"    → "Jan 2026" (day trimmed on purpose — a per-day
 *                     stamp on quarterly-refreshed data was misleading)
 *   ISO datetime    → "Jan 2026"
 *   bare "2026"     → "2026"
 *   null/empty      → null (caller should skip rendering)
 */

// Parse any of the accepted vintage strings into a Date the two
// public helpers can reason about. Returns null when the shape isn't
// recognisable, so callers know to bail. Kept private because the
// downstream helpers (label + stale) both need the exact same parse
// semantics and diverging them would drift the trust signal.
function _parseVintage(asOf) {
  if (!asOf) return null;
  const s = String(asOf).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Date.UTC(+iso[1], +iso[2] - 1, +iso[3]));
  const ym = s.match(/^(\d{4})-(\d{2})$/);
  if (ym) return new Date(Date.UTC(+ym[1], +ym[2] - 1, 1));
  const qm = s.match(/^(\d{4})-Q([1-4])$/i);
  if (qm) return new Date(Date.UTC(+qm[1], (+qm[2] - 1) * 3, 1));
  if (/^\d{4}$/.test(s)) return new Date(Date.UTC(+s, 0, 1));
  return null;
}

export function vintageLabelFor(asOf) {
  if (!asOf) return null;
  const s = String(asOf).trim();
  // YYYY-QN gets rendered in its original quarter form because
  // families understand "Q4 2025" better than "Oct 2025" for a
  // quarterly source refresh.
  const qMatch = s.match(/^(\d{4})-Q([1-4])$/i);
  if (qMatch) return `Q${qMatch[2]} ${qMatch[1]}`;
  // Bare year — pass through untouched.
  if (/^\d{4}$/.test(s)) return s;
  const dt = _parseVintage(s);
  if (!dt) return s;
  return dt.toLocaleDateString("en-GB", {
    month: "short", year: "numeric", timeZone: "UTC",
  });
}

/**
 * isVintageStale — heuristic mirroring the backend's 90-day refresh
 * cycle (`_compute_freshness` in routes/degrees.py). Used on the
 * client where the backend doesn't already ship an `is_stale` flag
 * next to a raw as_of (typically country cost-of-living rows).
 * Returns true when the parsed vintage is older than 180 days by
 * default — a slightly wider window than the firm-band 90-day rule
 * because cost-of-living data churns less than salary bands.
 *
 * The `now` argument is accepted for deterministic tests; production
 * callers should omit it.
 */
export function isVintageStale(asOf, cycleDays = 180, now = Date.now()) {
  const vintage = _parseVintage(asOf);
  if (!vintage) return false;
  const ageDays = (now - vintage.getTime()) / 86400000;
  return ageDays > cycleDays;
}
